/**
 * File: errorLogUtils.ts
 * Path: apps/admin/src/shared/utils/errorLogUtils.ts
 * Description: The last errors this device hit (crashes and failed
 * background work), kept in this browser only, so System > Diagnostics can
 * show what went wrong and the copied report can carry it.
 *
 * Nothing leaves the device unless someone copies the report. Messages are
 * trimmed and the list is capped, so it never grows.
 */

export interface LoggedError {
  at: number;
  message: string;
  /** The page it happened on. */
  path: string;
}

export const ERROR_LOG_KEY = "idk.errorLog";
export const ERROR_LOG_LIMIT = 20;
const MESSAGE_MAX = 300;

/** Storage can be missing or refuse (private windows): the log is a convenience, never a crash. */
const store = (): Storage | null => {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
};

export const readErrorLog = (): LoggedError[] => {
  try {
    const parsed: unknown = JSON.parse(store()?.getItem(ERROR_LOG_KEY) ?? "[]");
    return Array.isArray(parsed)
      ? parsed.filter(
          (e): e is LoggedError =>
            !!e && typeof e === "object" && typeof e.at === "number" && typeof e.message === "string" && typeof e.path === "string",
        )
      : [];
  } catch {
    return [];
  }
};

/** Adds one, newest first. A repeat of the newest within a few seconds is dropped (render loops). */
export const logError = (message: string, path: string, at: number = Date.now()): void => {
  const text = message.trim().slice(0, MESSAGE_MAX) || "Unknown error";
  const list = readErrorLog();
  if (list[0] && list[0].message === text && at - list[0].at < 5000) return;
  try {
    store()?.setItem(ERROR_LOG_KEY, JSON.stringify([{ at, message: text, path }, ...list].slice(0, ERROR_LOG_LIMIT)));
  } catch {
    // Full or refused: nothing to do.
  }
};

export const clearErrorLog = (): void => {
  try {
    store()?.removeItem(ERROR_LOG_KEY);
  } catch {
    // Nothing to do.
  }
};

const describe = (reason: unknown): string =>
  reason instanceof Error ? reason.message : typeof reason === "string" ? reason : "Unknown error";

/** Listens for crashes and unhandled failures for the life of the page. Call once, at start-up. */
export const startErrorLog = (target: Window = window): void => {
  target.addEventListener("error", (event) => {
    logError(event.error ? describe(event.error) : event.message, target.location.pathname);
  });
  target.addEventListener("unhandledrejection", (event) => {
    logError(describe(event.reason), target.location.pathname);
  });
};
